/**
 * ISO-3166 numeric → alpha-2 lookup for country polygons.
 *
 * The TopoJSON shipped by `world-atlas` (and the natural-earth derivative)
 * tags each country feature with a numeric ISO-3166-1 code as `id`.
 * We map that to the 2-letter ISO alpha-2 that `RegionDeepDive` and the
 * backend's `/api/dashboard/region/<code>` endpoint expect.
 *
 * Coverage: the ~120 countries relevant to logistics / supply-chain
 * analysis — every country with a coastline + the biggest landlocked
 * economies. Unknown numeric codes fall through to `null`, which surfaces
 * as a gracefully-handled 404 in the deep-dive modal.
 */

export const ISO_NUMERIC_TO_ALPHA2: Record<string, string> = {
  // Africa
  "012": "DZ", // Algeria
  "024": "AO", // Angola
  "204": "BJ", // Benin
  "072": "BW", // Botswana
  "854": "BF", // Burkina Faso
  "108": "BI", // Burundi
  "120": "CM", // Cameroon
  "132": "CV", // Cape Verde
  "140": "CF", // Central African Republic
  "148": "TD", // Chad
  "174": "KM", // Comoros
  "178": "CG", // Congo
  "180": "CD", // DR Congo
  "384": "CI", // Côte d'Ivoire
  "262": "DJ", // Djibouti
  "818": "EG", // Egypt
  "226": "GQ", // Equatorial Guinea
  "232": "ER", // Eritrea
  "231": "ET", // Ethiopia
  "266": "GA", // Gabon
  "270": "GM", // Gambia
  "288": "GH", // Ghana
  "324": "GN", // Guinea
  "624": "GW", // Guinea-Bissau
  "404": "KE", // Kenya
  "426": "LS", // Lesotho
  "430": "LR", // Liberia
  "434": "LY", // Libya
  "450": "MG", // Madagascar
  "454": "MW", // Malawi
  "466": "ML", // Mali
  "478": "MR", // Mauritania
  "480": "MU", // Mauritius
  "504": "MA", // Morocco
  "508": "MZ", // Mozambique
  "516": "NA", // Namibia
  "562": "NE", // Niger
  "566": "NG", // Nigeria
  "646": "RW", // Rwanda
  "686": "SN", // Senegal
  "694": "SL", // Sierra Leone
  "706": "SO", // Somalia
  "710": "ZA", // South Africa
  "728": "SS", // South Sudan
  "729": "SD", // Sudan
  "748": "SZ", // Eswatini
  "834": "TZ", // Tanzania
  "768": "TG", // Togo
  "788": "TN", // Tunisia
  "800": "UG", // Uganda
  "894": "ZM", // Zambia
  "716": "ZW", // Zimbabwe

  // Americas
  "032": "AR", // Argentina
  "044": "BS", // Bahamas
  "052": "BB", // Barbados
  "084": "BZ", // Belize
  "068": "BO", // Bolivia
  "076": "BR", // Brazil
  "124": "CA", // Canada
  "152": "CL", // Chile
  "170": "CO", // Colombia
  "188": "CR", // Costa Rica
  "192": "CU", // Cuba
  "214": "DO", // Dominican Republic
  "218": "EC", // Ecuador
  "222": "SV", // El Salvador
  "320": "GT", // Guatemala
  "328": "GY", // Guyana
  "332": "HT", // Haiti
  "340": "HN", // Honduras
  "388": "JM", // Jamaica
  "484": "MX", // Mexico
  "558": "NI", // Nicaragua
  "591": "PA", // Panama
  "600": "PY", // Paraguay
  "604": "PE", // Peru
  "630": "PR", // Puerto Rico
  "740": "SR", // Suriname
  "780": "TT", // Trinidad and Tobago
  "840": "US", // United States
  "858": "UY", // Uruguay
  "862": "VE", // Venezuela

  // Asia
  "004": "AF", // Afghanistan
  "051": "AM", // Armenia
  "031": "AZ", // Azerbaijan
  "048": "BH", // Bahrain
  "050": "BD", // Bangladesh
  "064": "BT", // Bhutan
  "096": "BN", // Brunei
  "116": "KH", // Cambodia
  "156": "CN", // China
  "196": "CY", // Cyprus
  "158": "TW", // Taiwan
  "268": "GE", // Georgia
  "344": "HK", // Hong Kong
  "356": "IN", // India
  "360": "ID", // Indonesia
  "364": "IR", // Iran
  "368": "IQ", // Iraq
  "376": "IL", // Israel
  "392": "JP", // Japan
  "400": "JO", // Jordan
  "398": "KZ", // Kazakhstan
  "408": "KP", // North Korea
  "410": "KR", // South Korea
  "414": "KW", // Kuwait
  "417": "KG", // Kyrgyzstan
  "418": "LA", // Laos
  "422": "LB", // Lebanon
  "458": "MY", // Malaysia
  "462": "MV", // Maldives
  "496": "MN", // Mongolia
  "104": "MM", // Myanmar
  "524": "NP", // Nepal
  "512": "OM", // Oman
  "586": "PK", // Pakistan
  "275": "PS", // Palestine
  "608": "PH", // Philippines
  "634": "QA", // Qatar
  "682": "SA", // Saudi Arabia
  "702": "SG", // Singapore
  "144": "LK", // Sri Lanka
  "760": "SY", // Syria
  "762": "TJ", // Tajikistan
  "764": "TH", // Thailand
  "626": "TL", // Timor-Leste
  "792": "TR", // Turkey
  "795": "TM", // Turkmenistan
  "784": "AE", // United Arab Emirates
  "860": "UZ", // Uzbekistan
  "704": "VN", // Vietnam
  "887": "YE", // Yemen

  // Europe
  "008": "AL", // Albania
  "020": "AD", // Andorra
  "040": "AT", // Austria
  "112": "BY", // Belarus
  "056": "BE", // Belgium
  "070": "BA", // Bosnia and Herzegovina
  "100": "BG", // Bulgaria
  "191": "HR", // Croatia
  "203": "CZ", // Czech Republic
  "208": "DK", // Denmark
  "233": "EE", // Estonia
  "246": "FI", // Finland
  "250": "FR", // France
  "276": "DE", // Germany
  "300": "GR", // Greece
  "348": "HU", // Hungary
  "352": "IS", // Iceland
  "372": "IE", // Ireland
  "380": "IT", // Italy
  "428": "LV", // Latvia
  "438": "LI", // Liechtenstein
  "440": "LT", // Lithuania
  "442": "LU", // Luxembourg
  "470": "MT", // Malta
  "498": "MD", // Moldova
  "492": "MC", // Monaco
  "499": "ME", // Montenegro
  "807": "MK", // North Macedonia
  "528": "NL", // Netherlands
  "578": "NO", // Norway
  "616": "PL", // Poland
  "620": "PT", // Portugal
  "642": "RO", // Romania
  "643": "RU", // Russia
  "688": "RS", // Serbia
  "703": "SK", // Slovakia
  "705": "SI", // Slovenia
  "724": "ES", // Spain
  "752": "SE", // Sweden
  "756": "CH", // Switzerland
  "804": "UA", // Ukraine
  "826": "GB", // United Kingdom
  "336": "VA", // Vatican

  // Oceania
  "036": "AU", // Australia
  "242": "FJ", // Fiji
  "554": "NZ", // New Zealand
  "598": "PG", // Papua New Guinea
  "090": "SB", // Solomon Islands
}

/**
 * Normalises a numeric id (which TopoJSON sometimes stores as a number,
 * sometimes as a zero-padded string) into the zero-padded 3-digit string
 * key used by `ISO_NUMERIC_TO_ALPHA2`.
 */
export function numericIdToAlpha2(id: string | number | undefined | null): string | null {
  if (id == null) return null
  const s = typeof id === "number" ? String(id) : id
  const padded = s.padStart(3, "0")
  return ISO_NUMERIC_TO_ALPHA2[padded] ?? null
}
